import { useUpdateEffect } from 'ahooks';
import { Form, FormProps } from 'antd';
import { FC, memo, ReactElement, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { useAppModalControls } from '@/common/hooks';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError, parseDate } from '@/common/utils';

import { SearchConsolePushIndexing } from '@/modules/Domains';
import {
  PageFormFields,
  PageFormInputs,
  PagesCriticalCssForm,
  PageUsageForBots,
  PageUsageForKeys,
  useEditPagesMutation,
  useGetByIdLayoutsPageMutation,
  useGetOneByIdPagesQuery,
  useUpdatePagesCriticalCssGenerationStatus,
} from '@/modules/Pages';

import { PagesFormView } from './PagesFormView';
import { pagesValidationSchema } from './validation';

interface Props {
  siteId: string;
  pageId: string;
}

export const PagesEditFormContainer: FC<Props> = memo(({ siteId, pageId }) => {
  const navigate = useNavigate();
  const [pushIndexingModalControls] = useAppModalControls();
  const [pageCriticalCssModalControls] = useAppModalControls();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageFormInputs>>(null);

  useUpdatePagesCriticalCssGenerationStatus(siteId, pageId);

  const {
    data: sitePageData,
    error: sitePageError,
    isLoading: isPageLoading,
    isError: isPageError,
    refetch: refetchPageData,
  } = useGetOneByIdPagesQuery(
    pageId,
    siteId,
  )({
    enabled: !!siteId && !!pageId,
    onSuccess: (data) => {
      form.setFieldsValue({
        title: data?.title,
        body: data?.body,
        usageFor: !('type' in (data?.usageFor ?? {})),
        publishedAt: data?.publishedAt ? parseDate(data.publishedAt) : undefined,
        structureNodeId: data?.structureNode.id,
        layoutId: data?.layout?.id,
      });
    },
  });

  const { mutateAsync: getLayoutMutation } = useGetByIdLayoutsPageMutation(siteId)({
    onMutate: () => {
      setFormErrors(null);
    },
    onSuccess: (data) => {
      handleSetupBlocks(data.id, data.blocks);
    },
    onError: (err) => {
      messageOnError(err, appMessageApi);
    },
  });

  const { mutate: editPageMutation, isLoading: editSiteIsLoading } = useEditPagesMutation(
    pageId,
    siteId,
  )({
    onSuccess: async () => {
      appMessageApi.success('Сторінку сайту змінено успішно!');

      await refetchPageData();
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageFormInputs>(err));
      messageOnError(err, appMessageApi);
    },
  });

  const initialValues: PageFormFields = {
    title: sitePageData?.title,
    body: sitePageData?.body,
    usageFor: !('type' in (sitePageData?.usageFor ?? {})),
    publishedAt: sitePageData?.publishedAt ? parseDate(sitePageData.publishedAt) : undefined,
    structureNodeId: sitePageData?.structureNode.id,
    layoutId: sitePageData?.layout?.id,
  };

  // useUpdateEffect(() => {
  //   form.setFieldsValue({
  //     title: sitePageData?.title,
  //     body: sitePageData?.body,
  //     usageFor: !('type' in (sitePageData?.usageFor ?? {})),
  //     publishedAt: sitePageData?.publishedAt ? parseDate(sitePageData.publishedAt) : undefined,
  //     structureNodeId: sitePageData?.structureNode.id,
  //     layoutId: sitePageData?.layout?.id,
  //     siteId
  //   });
  // }, [sitePageData, form]);

  const handleFinish = useCallback((values: PageFormFields): void => {
    setFormErrors(null);

    editPageMutation({
      ...values,
      usageFor: values.usageFor ? { [PageUsageForKeys.Bot]: PageUsageForBots.Google } : { type: 'default' },
    });
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.PagesById.replace(':siteId', siteId).replace(':pageId', pageId));
  }, [siteId, pageId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleKeyDown = useCallback<NonNullable<FormProps['onKeyDown']>>(
    (e) => {
      if (e.metaKey && e.key === 's') {
        e.preventDefault();
        form.submit();
      }
    },
    [form],
  );

  const handleSetupBlocks = (layoutId: string, blocks: string[]) => {
    form.setFieldValue(PageFormInputs.LayoutId, layoutId);

    if (layoutId) {
      if (blocks.length) {
        form.setFieldValue(
          PageFormInputs.Body,
          blocks.reduce(
            (acc, block) => {
              acc[block] = '';
              return acc;
            },
            {} as Record<string, string>,
          ),
        );
      } else {
        form.setFieldValue(PageFormInputs.Body, {});
      }
    } else {
      form.setFieldValue(PageFormInputs.Body, { content: '' });
    }
  };

  const handleOpenPushIndexingModal = (): void => {
    pushIndexingModalControls.open();
  };

  const handleOpenCriticalCssModal = (): void => {
    pageCriticalCssModalControls.open();
  };

  const handleGetLayoutMutation = async (id: string) => {
    await getLayoutMutation(id);
  };

  return (
    <FallbackedLayout data={sitePageData} isLoading={isPageLoading} isError={isPageError} error={sitePageError}>
      {(sitePage): ReactElement => (
        <>
          <SearchConsolePushIndexing modalControls={pushIndexingModalControls} siteId={siteId} pageId={pageId} />

          <PagesCriticalCssForm modalControls={pageCriticalCssModalControls} siteId={siteId} pageId={pageId} />

          <PagesFormView
            formName="page-edit"
            formType="edit"
            formErrors={formErrors}
            form={form}
            pageId={pageId}
            nodeId={sitePage.structureNode.id}
            pages={sitePage.structureNode.pages}
            pageCriticalCssStatus={sitePage.pageCriticalCss.generationStatus}
            siteId={siteId}
            previewLink={sitePage.previewLink}
            structureNodeInfo={{
              id: sitePage.structureNode.id,
              path: sitePage.structureNode.path,
            }}
            siteHaveSearchConsole={sitePage.structureNode.site.siteHaveSearchConsole}
            updatedAt={sitePage.updatedAt}
            initialValues={initialValues}
            isFormLoading={editSiteIsLoading}
            validationSchema={pagesValidationSchema}
            handleKeyDown={handleKeyDown}
            handleBack={handleBack}
            handleFinish={handleFinish}
            handleFinishFailed={handleFinishFailed}
            handleGetLayoutMutation={handleGetLayoutMutation}
            handleOpenPushIndexingModal={handleOpenPushIndexingModal}
            handleOpenCriticalCssModal={handleOpenCriticalCssModal}
          />
        </>
      )}
    </FallbackedLayout>
  );
});
